import {
  Popover,
  PopoverAnchor,
  PopoverBody,
  PopoverContent,
  Portal,
  useMergeRefs,
} from "@chakra-ui/react";
import { forwardRef } from "@chakra-ui/system";
import { CalendarDate } from "@uselessdev/datepicker";
import { CalendarValues } from "@uselessdev/datepicker/dist/declarations/types/src/types";
import * as chrono from "chrono-node";
import { formatISO, parseISO } from "date-fns";
import React, { useCallback } from "react";
import { useController } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { usePrefixedFieldName } from "shared/components/form/FormFieldPrefix";
import { useIsFormDisabled } from "shared/components/form/FormStateProvider";
import { CalendarView } from "shared/components/input/date/CalendarView";
import { formatDateDisplayIfValid } from "shared/components/input/date/DateInputUtils";
import {
  SimpleTextInput,
  SimpleTextInputProps,
} from "shared/components/input/text/SimpleTextInput";
import { useInputPopover } from "shared/components/input/UseInputPopover";
import { PopoverOverlay } from "shared/components/popover/PopoverOverlay";
import { getFieldError } from "shared/utils/FormUtils";
import zIndices from "theme/zIndices";

export interface SimpleDateInputProps
  extends Omit<SimpleTextInputProps, "value" | "onChangeValue"> {
  value: Date | undefined;
  onChangeValue: (value: Date | undefined) => void;
}

export type FormSimpleDateInputProps = {
  name: string;
  shouldUnregister?: boolean;
  onChangeValue?: (value: string) => void;
} & Omit<SimpleDateInputProps, "name" | "value" | "error" | "onChangeValue">;

export const SimpleDateInput = forwardRef<SimpleDateInputProps, "input">(
  ({ value, onChangeValue, isDisabled, isReadOnly, ...props }, fwInputRef) => {
    const { t } = useTranslation(["common"]);

    const {
      inputRef,
      popoverProps,
      popoverContentProps,
      inputProps,
      setPendingValue,
      onClose,
      isOpen,
    } = useInputPopover<Date>({
      value,
      onChangeValue,
      isDisabled: isDisabled || isReadOnly,
      getValueFromInput: (inputValue) => chrono.parseDate(inputValue)!,
      getDisplayValue: (selectedValue) =>
        formatDateDisplayIfValid(selectedValue),
      updateWhileTyping: true,
    });

    const mergedInputRef = useMergeRefs(inputRef, fwInputRef);

    const onSelectDate = useCallback(
      (selectedValue: CalendarDate | CalendarValues) => {
        onChangeValue(selectedValue as Date);
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error FIXME: See comments in CalendarViewMonthYear#onPickerSelectDate
        if (!selectedValue.keepCalendarOpen) {
          // Add a slight delay to prevent flickering.
          setTimeout(onClose, 100);
        } else {
          setPendingValue(formatDateDisplayIfValid(selectedValue as Date));
        }
      },
      [onChangeValue, onClose, setPendingValue],
    );

    return (
      <Popover {...popoverProps}>
        <PopoverAnchor>
          <SimpleTextInput
            ref={mergedInputRef}
            placeholder={t("common:enterDate")}
            isReadOnly={isReadOnly}
            isDisabled={isDisabled}
            controlProps={{
              // Put above PopoverOverlay to allow interaction with clear button
              zIndex: isOpen ? zIndices.popoverOverlay + 10 : undefined,
            }}
            {...inputProps}
            {...props}
          />
        </PopoverAnchor>
        <PopoverOverlay />
        {/* Using Portal avoids messing up the input tab order */}
        <Portal>
          <PopoverContent
            {...popoverContentProps}
            p={0}
            w="min-content"
            border="none"
            outline="none"
            overflow="hidden"
            _focus={{ boxShadow: "none" }}
          >
            <PopoverBody p={0}>
              <CalendarView onSelectDate={onSelectDate} value={value as Date} />
            </PopoverBody>
          </PopoverContent>
        </Portal>
      </Popover>
    );
  },
);

export const FormSimpleDateInput = React.forwardRef<
  HTMLInputElement,
  FormSimpleDateInputProps
>(
  (
    {
      name,
      isDisabled,
      defaultValue = "",
      shouldUnregister = true,
      onChangeValue,
      ...otherProps
    },
    fwRef,
  ) => {
    const prefixedName = usePrefixedFieldName(name);
    const {
      field: { ref: fieldRef, value, onChange, ...field },
      formState: { errors },
    } = useController({
      name: prefixedName,
      shouldUnregister,
      defaultValue,
    });
    const error = getFieldError(errors, prefixedName)?.message;
    const mergedRef = useMergeRefs(fieldRef, fwRef);
    const isFormDisabled = useIsFormDisabled();
    return (
      <SimpleDateInput
        ref={mergedRef}
        {...field}
        {...otherProps}
        isInvalid={!!error} // FIXME: Remove this prop, use error instead
        isDisabled={isDisabled || isFormDisabled}
        error={error}
        value={value ? parseISO(value) : undefined}
        onChangeValue={(value) => {
          const newValue = value ? formatISO(value) : "";
          onChange(newValue);
          onChangeValue?.(newValue);
        }}
      />
    );
  },
);
FormSimpleDateInput.displayName = "FormSimpleDateInput";
